import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { filterTextParameterOptions } from '../../../shared/lib/revit/parameterOptionTypeFilter';
import { describeUndoPlanAffordance } from '../../../shared/lib/parameterUndo/undoAffordance';
import { Box } from '@mui/material';
import { CaptionText } from '../../../shared/ui/typography';
import MainLayout from '../../../shared/ui/layout/MainLayout';
import { layoutGap } from '../../../theme/theme';
import { OperationProgressWindowRequest } from '../../../shared/ui/progress';
import WizardSnackbarAlert from '../../../shared/ui/workflow/WizardSnackbarAlert';
import CompactFieldRow from '../../../shared/ui/workflow/CompactFieldRow';
import WorkflowFooter from '../../../shared/ui/workflow/WorkflowFooter';
import { AVAILABILITY_RECHECK_DEBOUNCE_MS, useWorkflowAvailabilityProgress } from '../lib/useWorkflowAvailabilityProgress';
import WorkflowAvailabilityProgress from './WorkflowAvailabilityProgress';
import {
  ActiveSectionHeader,
  EditorSurface,
  MappingFieldGrid,
  type SectionSummaryItem,
  SectionSwitcher,
  WorkflowNotice,
  WorkflowStepShell,
} from '../../../shared/ui/workflow/WorkflowStepComponents';
import HelpTooltipTrigger from '../../../shared/ui/common/HelpTooltipTrigger';
import ParameterSetupScopePanel, {
  formatParameterSetupTargetLabel,
  ProjectCategoryToggleStrip,
} from '../../../shared/ui/workflow/ParameterSetupScopePanel';
import { ValidationNavigationNotice } from '../../pcf-validation';
import {
  useAdditionalCategoriesConfiguration,
  useConnectorBindingConfiguration,
  useMenuConfiguration,
  useFormValidation,
  useParameterAddition,
  checkAdditionalCategoryParametersAvailability,
} from '../../../shared/lib';
import { useUIStore } from '../../../shared/stores/useUIStore';
import { useConfigurationStore } from '../../../shared/stores/useConfigurationStore';
import { usePcfValidationStore } from '../../../shared/stores/usePcfValidationStore';
import { useActiveProjectKey } from '../../../shared/hooks/ActiveDocumentProvider';
import { ADDITIONAL_CATEGORIES_FIELDS } from '../../../shared/config/validationConfig';
import { additionalCategoryTooltips } from '../../../shared/config/wizardTooltips';
import { resolveAdditionalCategoriesDestinationFocus } from '../../pcf-validation';
import {
  ADDITIONAL_CATEGORIES_PROJECT_PARAMETER_CATEGORY_OPTIONS,
  DEFAULT_ADDITIONAL_CATEGORIES_PROJECT_PARAMETER_CATEGORIES,
} from '../../../shared/types';
import { getContinueToNextWorkflowStepText } from '../../../shared/config/workflowUi';
import { useParameterDropdownOptions } from '../lib/useParameterDropdownOptions';

interface AdditionalCategoriesPageProps {
  onGoBack: () => void;
  onContinue: () => void;
}

type SectionKey = 'valve' | 'supports' | 'instrument';
type FieldKey = 'valveTagValue' | 'supportsTagValue' | 'supportsSsrefValue' | 'instrumentTagValue';

interface CategoryFieldDefinition {
  field: FieldKey;
  label: string;
  required: boolean;
  help: (typeof additionalCategoryTooltips)[keyof typeof additionalCategoryTooltips];
  focusTokens: string[];
}

interface SectionDefinition {
  key: SectionKey;
  title: string;
  shortTitle: string;
  description: string;
  fields: readonly CategoryFieldDefinition[];
}

const SECTION_DEFINITIONS: readonly SectionDefinition[] = [
  {
    key: 'valve',
    title: 'VALVE',
    shortTitle: 'VALVE',
    description: 'Map the Revit parameter that provides the valve identifier for ISO output.',
    fields: [
      {
        field: 'valveTagValue',
        label: 'TAG *',
        required: true,
        help: additionalCategoryTooltips.valveTag,
        focusTokens: ['VALVE', 'VALVE-TAG'],
      },
    ],
  },
  {
    key: 'supports',
    title: 'SUPPORTS',
    shortTitle: 'SUPPORTS',
    description: 'Map the support identifier and optional standard support reference for ISO output.',
    fields: [
      {
        field: 'supportsTagValue',
        label: 'TAG *',
        required: true,
        help: additionalCategoryTooltips.supportsTag,
        focusTokens: ['SUPPORTS', 'SUPPORTS-TAG'],
      },
      {
        field: 'supportsSsrefValue',
        label: 'SSREF',
        required: false,
        help: additionalCategoryTooltips.supportsSsref,
        focusTokens: ['SSREF'],
      },
    ],
  },
  {
    key: 'instrument',
    title: 'INSTRUMENT',
    shortTitle: 'INSTRUMENT',
    description: 'Map the Revit parameter that supplies the instrument identifier in ISO output.',
    fields: [
      {
        field: 'instrumentTagValue',
        label: 'TAG *',
        required: true,
        help: additionalCategoryTooltips.instrumentTag,
        focusTokens: ['INSTRUMENT', 'INSTRUMENT-TAG'],
      },
    ],
  },
];

const FIELD_SECTION_LOOKUP: Record<FieldKey, SectionKey> = {
  valveTagValue: 'valve',
  supportsTagValue: 'supports',
  supportsSsrefValue: 'supports',
  instrumentTagValue: 'instrument',
};

const AdditionalCategoriesPage: React.FC<AdditionalCategoriesPageProps> = ({
  onGoBack,
  onContinue,
}) => {
  const {
    config,
    updateField,
    setParameterTarget,
    setProjectParameterCategories,
  } = useAdditionalCategoriesConfiguration();
  const { config: menuConfig } = useMenuConfiguration();
  const { config: connectorConfig } = useConnectorBindingConfiguration();
  const syncAdditionalCategoriesToMapping = useConfigurationStore(
    (state) => state.syncAdditionalCategoriesToMapping
  );
  const defaultPropertiesConfirmed = useUIStore((state) => state.defaultPropertiesConfirmed);
  const setDefaultPropertiesConfirmed = useUIStore((state) => state.setDefaultPropertiesConfirmed);
  const setAdditionalCategoryParameterUndoPlan = useUIStore(
    (state) => state.setAdditionalCategoryParameterUndoPlan
  );
  const selectedOption = useUIStore((state) => state.selectedOption);
  const activeProjectKey = useActiveProjectKey();
  const activeSection = useUIStore((state) => state.sections.additionalCategories);
  const setActiveSection = useUIStore((state) => state.setAdditionalCategoriesSection);
  const issueNavigationContext = usePcfValidationStore((state) => state.issueNavigationContext);
  const { options: dropdownParameterOptions } = useParameterDropdownOptions(activeProjectKey);
  // All additional-category fields hold text values, so only text-typed parameters
  // are offered; unknown-typed options stay visible (parameterOptionTypeFilter).
  const textParameterOptions = useMemo(
    () => filterTextParameterOptions(dropdownParameterOptions),
    [dropdownParameterOptions]
  );
  const [autoDefaultPropertiesConfirmed, setAutoDefaultPropertiesConfirmed] = useState(false);
  const [
    autoDefaultPropertiesConfirmedProjectKey,
    setAutoDefaultPropertiesConfirmedProjectKey,
  ] = useState<string | null>(null);
  const autoDefaultPropertiesConfirmedRef = useRef(autoDefaultPropertiesConfirmed);
  const activeProjectKeyRef = useRef(activeProjectKey);
  const availabilityStructuralKeyRef = useRef<string | null>(null);
  const [notification, setNotification] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });

  useEffect(() => {
    autoDefaultPropertiesConfirmedRef.current = autoDefaultPropertiesConfirmed;
  }, [autoDefaultPropertiesConfirmed]);

  useEffect(() => {
    activeProjectKeyRef.current = activeProjectKey;
  }, [activeProjectKey]);

  const {
    validation,
    getFieldError,
    markFieldTouched,
    showValidationErrors,
    setShowValidationErrors,
    canProceed,
  } = useFormValidation({
    config,
    fields: ADDITIONAL_CATEGORIES_FIELDS,
  });

  const {
    isAdding: isAddingParameters,
    parameterProgress,
    addAdditionalCategoryParameters,
    undoLastAdditionalCategoryParameterAddition,
    lastAdditionalCategoryUndoPlan,
  } = useParameterAddition();
  const autoDefaultPropertiesConfirmedForActiveProject =
    autoDefaultPropertiesConfirmed &&
    autoDefaultPropertiesConfirmedProjectKey === activeProjectKey;
  const setupConfirmed = defaultPropertiesConfirmed || autoDefaultPropertiesConfirmedForActiveProject;
  const selectedTargetLabel = formatParameterSetupTargetLabel(
    config.parameterDestination,
    config.parameterBindingKind
  );
  const selectedProjectParameterCategories = useMemo(() => {
    const configuredCategories = Array.isArray(config.projectParameterCategories)
      ? config.projectParameterCategories
      : DEFAULT_ADDITIONAL_CATEGORIES_PROJECT_PARAMETER_CATEGORIES;
    const allowedCategories = new Set<string>(
      ADDITIONAL_CATEGORIES_PROJECT_PARAMETER_CATEGORY_OPTIONS
    );
    const normalizedCategories = Array.from(
      new Set(
        configuredCategories.filter(
          (category) => typeof category === 'string' && allowedCategories.has(category)
        )
      )
    );

    return normalizedCategories.length > 0
      ? normalizedCategories
      : DEFAULT_ADDITIONAL_CATEGORIES_PROJECT_PARAMETER_CATEGORIES;
  }, [config.projectParameterCategories]);
  const additionalCategoryTargetApplication =
    config.parameterDestination === 'family'
      ? `${selectedTargetLabel} applies to editable VALVE, SUPPORTS, and INSTRUMENT families.`
      : `${selectedTargetLabel} applies to the selected Revit categories.`;
  const {
    isCheckingAvailability,
    availabilityProgress,
    beginAvailabilityCheck,
    updateAvailabilityProgressFromHost,
    endAvailabilityCheck,
  } = useWorkflowAvailabilityProgress({
    title: 'Checking category parameter availability',
    message: 'Revit is checking identifier parameter names, targets, and bindings.',
    detail: 'This availability check does not generate ISO output or prove runtime export success.',
    completeMessage: 'Category parameter availability check complete',
    errorMessage: 'Category parameter availability could not be checked',
  });
  const additionalCategoryUndoAffordance = describeUndoPlanAffordance(
    lastAdditionalCategoryUndoPlan
  );
  const hasUndoableAdditionalCategoryAddition =
    Boolean(activeProjectKey) &&
    lastAdditionalCategoryUndoPlan?.projectKey === activeProjectKey &&
    additionalCategoryUndoAffordance.visible;
  const continueToNextStepText = useMemo(
    () => getContinueToNextWorkflowStepText('additionalCategories', selectedOption),
    [selectedOption]
  );

  useEffect(() => {
    let isActive = true;
    let checkId: number | null = null;
    const availabilityProjectKey = activeProjectKey;
    const structuralKey = `${availabilityProjectKey ?? ''}|${config.parameterDestination}|${config.parameterBindingKind}|${menuConfig.objectTypeValue}|${connectorConfig.parameterDestination}|${connectorConfig.parameterBindingKind}`;
    const isStructuralChange = availabilityStructuralKeyRef.current !== structuralKey;
    availabilityStructuralKeyRef.current = structuralKey;

    if (autoDefaultPropertiesConfirmedRef.current) {
      autoDefaultPropertiesConfirmedRef.current = false;
      setAutoDefaultPropertiesConfirmed(false);
      setAutoDefaultPropertiesConfirmedProjectKey(null);
    }

    const checkAvailability = async (silent: boolean) => {
      const currentCheckId = beginAvailabilityCheck({ silent });
      checkId = currentCheckId;
      try {
        const isAvailable = await checkAdditionalCategoryParametersAvailability(
          config,
          menuConfig.objectTypeValue,
          {
            cacheScopeKey: availabilityProjectKey,
            onProgress: silent
              ? undefined
              : (progress) => updateAvailabilityProgressFromHost(currentCheckId, progress),
          }
        );
        if (
          isActive &&
          activeProjectKeyRef.current === availabilityProjectKey &&
          autoDefaultPropertiesConfirmedRef.current !== isAvailable
        ) {
          setAutoDefaultPropertiesConfirmed(isAvailable);
          setAutoDefaultPropertiesConfirmedProjectKey(isAvailable ? availabilityProjectKey : null);
        }
      } finally {
        endAvailabilityCheck(currentCheckId);
      }
    };

    if (isStructuralChange) {
      void checkAvailability(false).catch(() => undefined);
      return () => {
        isActive = false;
        if (checkId !== null) {
          endAvailabilityCheck(checkId);
        }
      };
    }

    // Field edits re-check silently once typing settles; a modal check per
    // keystroke steals focus and closes the mapping dropdowns.
    const debounceTimer = globalThis.setTimeout(() => {
      void checkAvailability(true).catch(() => undefined);
    }, AVAILABILITY_RECHECK_DEBOUNCE_MS);

    return () => {
      isActive = false;
      globalThis.clearTimeout(debounceTimer);
      if (checkId !== null) {
        endAvailabilityCheck(checkId);
      }
    };
  }, [
    activeProjectKey,
    beginAvailabilityCheck,
    config,
    connectorConfig,
    endAvailabilityCheck,
    menuConfig.objectTypeValue,
    updateAvailabilityProgressFromHost,
  ]);

  const validationFocusFields = useMemo(
    () =>
      new Set(
        (issueNavigationContext?.actionParams?.additionalCategoryFields || issueNavigationContext?.affectedItems || [])
          .map((item) => item.trim().toUpperCase().replace(/[\s_]+/g, '-'))
          .filter(Boolean)
      ),
    [issueNavigationContext?.actionParams?.additionalCategoryFields, issueNavigationContext?.affectedItems]
  );

  const isValidationFocused = useCallback(
    (...tokens: string[]) =>
      tokens.some((token) => validationFocusFields.has(token.trim().toUpperCase().replace(/[\s_]+/g, '-'))),
    [validationFocusFields]
  );

  const invalidateAdditionalCategoryParameterSetup = useCallback(() => {
    autoDefaultPropertiesConfirmedRef.current = false;
    setAutoDefaultPropertiesConfirmed(false);
    setAutoDefaultPropertiesConfirmedProjectKey(null);
    setDefaultPropertiesConfirmed(false);
    setAdditionalCategoryParameterUndoPlan(null);
  }, [setAdditionalCategoryParameterUndoPlan, setDefaultPropertiesConfirmed]);

  const fieldHandlers = useMemo<Record<FieldKey, (value: string) => void>>(
    () => ({
      valveTagValue: (value) => {
        updateField('valveTagValue', value);
        markFieldTouched('valveTagValue');
        invalidateAdditionalCategoryParameterSetup();
      },
      supportsTagValue: (value) => {
        updateField('supportsTagValue', value);
        markFieldTouched('supportsTagValue');
        invalidateAdditionalCategoryParameterSetup();
      },
      supportsSsrefValue: (value) => {
        updateField('supportsSsrefValue', value);
        markFieldTouched('supportsSsrefValue');
        invalidateAdditionalCategoryParameterSetup();
      },
      instrumentTagValue: (value) => {
        updateField('instrumentTagValue', value);
        markFieldTouched('instrumentTagValue');
        invalidateAdditionalCategoryParameterSetup();
      },
    }),
    [invalidateAdditionalCategoryParameterSetup, markFieldTouched, updateField]
  );

  const sectionStates = useMemo(
    () =>
      SECTION_DEFINITIONS.map((section) => {
        const configuredCount = section.fields.filter((f) => (config[f.field].value || '').trim()).length;
        const requiredFields = section.fields.filter((f) => f.required);
        const requiredComplete = requiredFields.filter((f) => (config[f.field].value || '').trim()).length;
        const missingRequiredFields = requiredFields.filter((f) => !(config[f.field].value || '').trim());
        const errorMessages = section.fields
          .map((f) => validation.errors[f.field])
          .filter((m): m is string => Boolean(m));

        return {
          ...section,
          configuredCount,
          requiredCount: requiredFields.length,
          requiredComplete,
          missingRequiredFields,
          hasBlockingIssue: missingRequiredFields.length > 0 || errorMessages.length > 0,
          isComplete: missingRequiredFields.length === 0 && configuredCount > 0,
          errorMessages,
        };
      }),
    [config, validation.errors]
  );

  const activeSectionState = useMemo(
    () => sectionStates.find((s) => s.key === activeSection) ?? sectionStates[0],
    [activeSection, sectionStates]
  );

  const overallProgress = useMemo(() => {
    const totalRequired = sectionStates.reduce((sum, s) => sum + s.requiredCount, 0);
    const completedRequired = sectionStates.reduce((sum, s) => sum + s.requiredComplete, 0);
    return { totalRequired, completedRequired, percent: totalRequired > 0 ? Math.round((completedRequired / totalRequired) * 100) : 0 };
  }, [sectionStates]);

  const blockingReasons = useMemo(() => {
    const reasons: string[] = [];
    if (!setupConfirmed) reasons.push('confirm creation of missing Revit parameters');
    if (!validation.isValid) {
      const firstError = sectionStates.find(s => s.errorMessages.length > 0)?.errorMessages[0];
      if (firstError) {
        reasons.push(firstError);
      } else if (validation.missingFields.length > 0) {
        reasons.push(`${validation.missingFields.length} required mapping${validation.missingFields.length === 1 ? '' : 's'} missing`);
      }
    }
    return reasons;
  }, [sectionStates, setupConfirmed, validation.isValid, validation.missingFields.length]);

  const footerContent = useMemo(() => {
    return (
      <WorkflowFooter
        isReady={blockingReasons.length === 0}
        readyText={`${overallProgress.completedRequired}/${overallProgress.totalRequired} mapped`}
        blockedReasons={blockingReasons}
      />
    );
  }, [blockingReasons, overallProgress]);

  const handleDefaultPropertiesChange = useCallback(
    (checked: boolean) => {
      setDefaultPropertiesConfirmed(checked);
    },
    [setDefaultPropertiesConfirmed]
  );

  const handleTargetChange = useCallback(
    (target: { destination: 'project' | 'family'; bindingKind: 'type' | 'instance' }) => {
      setParameterTarget(target);
      invalidateAdditionalCategoryParameterSetup();
    },
    [invalidateAdditionalCategoryParameterSetup, setParameterTarget]
  );

  const handleAdditionalProjectCategoryChange = useCallback(
    (category: string, checked: boolean) => {
      const nextCategories = checked
        ? Array.from(new Set([...selectedProjectParameterCategories, category]))
        : selectedProjectParameterCategories.filter(
            (currentCategory) => currentCategory !== category
          );

      setProjectParameterCategories(nextCategories);
      invalidateAdditionalCategoryParameterSetup();
    },
    [
      invalidateAdditionalCategoryParameterSetup,
      selectedProjectParameterCategories,
      setProjectParameterCategories,
    ]
  );

  const handleUndoLastAddition = useCallback(async () => {
    await undoLastAdditionalCategoryParameterAddition();
  }, [undoLastAdditionalCategoryParameterAddition]);

  const handleContinue = useCallback(async () => {
    if (!validation.isValid) {
      setShowValidationErrors(true);
      const invalidField = Object.keys(validation.errors)[0] as FieldKey | undefined;
      if (invalidField && FIELD_SECTION_LOOKUP[invalidField]) {
        setActiveSection(FIELD_SECTION_LOOKUP[invalidField]);
      }
      setNotification({
        open: true,
        message: `Complete the required category mappings: ${validation.missingFields.join(', ')}`,
      });
      return;
    }

    if (!setupConfirmed) return;

    syncAdditionalCategoriesToMapping();
    const result = await addAdditionalCategoryParameters();
    if (!result.success) {
      return;
    }

    onContinue();
  }, [
    addAdditionalCategoryParameters,
    onContinue,
    setActiveSection,
    setShowValidationErrors,
    setupConfirmed,
    syncAdditionalCategoriesToMapping,
    validation.errors,
    validation.isValid,
    validation.missingFields,
  ]);

  const handleNotificationClose = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  useEffect(() => {
    if (issueNavigationContext?.targetPage !== 'properties') return;
    const focusedItems = issueNavigationContext.actionParams?.additionalCategoryFields || issueNavigationContext.affectedItems;
    const nextFocus = issueNavigationContext.actionParams?.additionalCategorySections
      ? {
          valve: issueNavigationContext.actionParams.additionalCategorySections.includes('valve'),
          supports: issueNavigationContext.actionParams.additionalCategorySections.includes('supports'),
          instrument: issueNavigationContext.actionParams.additionalCategorySections.includes('instrument'),
        }
      : resolveAdditionalCategoriesDestinationFocus(focusedItems);
    setActiveSection(getSectionFromFocusState(nextFocus));
  }, [
    issueNavigationContext?.familyId,
    issueNavigationContext?.targetPage,
    issueNavigationContext?.affectedItems,
    issueNavigationContext?.actionParams?.additionalCategoryFields,
    issueNavigationContext?.actionParams?.additionalCategorySections,
    setActiveSection,
  ]);

  const canProceedWithForm = canProceed(setupConfirmed && !isAddingParameters);

  const sectionSummaryItems = useMemo<SectionSummaryItem[]>(
    () =>
      sectionStates.map((section) => {
        const statusTone: SectionSummaryItem['statusTone'] = section.isComplete
          ? 'ready'
          : section.hasBlockingIssue
            ? 'missing'
            : 'optional';

        return {
        key: section.key,
        title: section.shortTitle,
        detail: section.description,
        help:
          section.key === 'valve'
            ? additionalCategoryTooltips.valveSection
            : section.key === 'supports'
              ? additionalCategoryTooltips.supportsSection
              : additionalCategoryTooltips.instrumentSection,
        helpLabel: `${section.shortTitle} additional-category section`,
        statusLabel: section.isComplete
          ? 'Ready'
          : section.hasBlockingIssue
            ? 'Needs attention'
            : `${section.configuredCount}/${section.fields.length} set`,
        statusTone,
        selected: activeSection === section.key,
        onClick: () => setActiveSection(section.key),
        role: 'tab',
        ariaSelected: activeSection === section.key,
        ariaControls: `additional-categories-panel-${section.key}`,
        id: `additional-categories-tab-${section.key}`,
        tabIndex: activeSection === section.key ? 0 : -1,
      };
    }),
    [activeSection, sectionStates, setActiveSection]
  );

  const activeSectionStatusLabel = activeSectionState.isComplete
    ? 'Ready'
    : activeSectionState.hasBlockingIssue
      ? 'Needs attention'
      : `${activeSectionState.configuredCount}/${activeSectionState.fields.length} set`;
  const activeSectionStatusTone = activeSectionState.isComplete
    ? 'ready'
    : activeSectionState.hasBlockingIssue
      ? 'missing'
      : 'optional';

  const getSectionFromFocusState = (focusState: {
    valve: boolean;
    supports: boolean;
    instrument: boolean;
  }): SectionKey => {
    if (focusState.supports) return 'supports';
    if (focusState.instrument) return 'instrument';
    return 'valve';
  };

  return (
    <>
      <OperationProgressWindowRequest
        id="workflow-additional-category-parameter-addition"
        open={isAddingParameters}
        priority={100}
        model={parameterProgress ?? {
          title: 'Creating category parameters',
          message: 'Revit is creating the missing category identifier parameters.',
          detail: additionalCategoryTargetApplication,
          status: 'running',
          percent: null,
          locked: true,
        }}
      />

      <MainLayout
        title="Additional categories"
        maxWidth={960}
        maxHeight="92vh"
        showBack
        onBack={onGoBack}
        showNext
        onNext={handleContinue}
        nextText={
          isAddingParameters
            ? 'Adding…'
            : hasUndoableAdditionalCategoryAddition
              ? continueToNextStepText
              : 'Continue'
        }
        nextDisabled={!canProceedWithForm}
        compactWorkflowChrome
        workflowChromeVariant="wizard"
        showRuntimeBadgeInUtilityChrome={false}
        footerContent={footerContent}
      >
        <ValidationNavigationNotice applicableTargetPages={['properties']} />

        <WorkflowStepShell sx={{ width: '100%', py: 0.75 }}>
          <WorkflowAvailabilityProgress
            visible={isCheckingAvailability}
            suppressed={isAddingParameters}
            model={availabilityProgress}
          />

          {showValidationErrors && !validation.isValid && (
            <WorkflowNotice severity="warning" title="Complete the required category mappings">
              <CaptionText>
                Missing: {validation.missingFields.join(', ')}
              </CaptionText>
            </WorkflowNotice>
          )}

          <Box sx={{ display: 'grid', gap: layoutGap.row, minWidth: 0 }}>
            <ParameterSetupScopePanel
              layout="minimal"
              eyebrow=""
              title="Create missing category parameters"
              description="Choose where Revit stores valve, support, and instrument identifiers and whether their values are defined by TYPE or INSTANCE. These identifiers are used as ISO references in the PCF profile."
              statusLabel={setupConfirmed ? (autoDefaultPropertiesConfirmedForActiveProject ? 'Ready' : 'Set') : 'Missing'}
              statusTone={setupConfirmed ? 'set' : 'missing'}
              blocker={!setupConfirmed ? 'Continue blocked: confirm creation of missing Revit parameters' : null}
              target={{
                destination: config.parameterDestination,
                bindingKind: config.parameterBindingKind,
              }}
              recommendedTarget={{ destination: 'project', bindingKind: 'instance' }}
              onTargetChange={handleTargetChange}
              confirmed={Boolean(setupConfirmed)}
              autoConfirmed={Boolean(autoDefaultPropertiesConfirmedForActiveProject)}
              onConfirmedChange={handleDefaultPropertiesChange}
              modeLegend="Category parameter mode"
              modeAriaLabel="Additional-category parameter mode"
              setupHelp={additionalCategoryTooltips.setupPanel}
              modeHelp={
                <HelpTooltipTrigger
        label="Additional-category parameter mode choices"
                  help={additionalCategoryTooltips.parameterMode}
                  placement="bottom-start"
                />
              }
              notice={additionalCategoryTargetApplication}
              noticeHelp={
                <HelpTooltipTrigger
        label="Additional-category target behavior"
                  help={additionalCategoryTooltips.targetBehavior}
                  placement="bottom-start"
                />
              }
              appliedStatus={{
                visible: hasUndoableAdditionalCategoryAddition,
                message: additionalCategoryUndoAffordance.message,
                severity: additionalCategoryUndoAffordance.severity,
                onRevert: additionalCategoryUndoAffordance.canRevert
                  ? () => void handleUndoLastAddition()
                  : undefined,
                revertHelp: additionalCategoryTooltips.revertSetup,
              }}
            >
              {config.parameterDestination === 'project' ? (
                <ProjectCategoryToggleStrip
                  categories={ADDITIONAL_CATEGORIES_PROJECT_PARAMETER_CATEGORY_OPTIONS}
                  selectedCategories={selectedProjectParameterCategories}
                  onCategoryChange={handleAdditionalProjectCategoryChange}
                  help={additionalCategoryTooltips.projectCategories}
        helpLabel="Additional-category project categories"
                />
              ) : null}
            </ParameterSetupScopePanel>

            <SectionSwitcher
              ariaLabel="Additional category sections"
              items={sectionSummaryItems}
              presentation="tabs"
            />

            <EditorSurface sx={{ p: 1, minWidth: 0 }}>
              <Box
                role="tabpanel"
                id={`additional-categories-panel-${activeSectionState.key}`}
                aria-labelledby={`additional-categories-tab-${activeSectionState.key}`}
                sx={{ display: 'grid', gap: layoutGap.row }}
              >
                <ActiveSectionHeader
                  title={activeSectionState.title}
                  description={activeSectionState.description}
                  statusLabel={activeSectionStatusLabel}
                  statusTone={activeSectionStatusTone}
                />

                {activeSectionState.hasBlockingIssue && (
                  <WorkflowNotice severity="warning">
                    {activeSectionState.missingRequiredFields.length > 0
                      ? `${activeSectionState.missingRequiredFields.length} required field${activeSectionState.missingRequiredFields.length === 1 ? '' : 's'} missing`
                      : activeSectionState.errorMessages[0]}
                  </WorkflowNotice>
                )}

                <MappingFieldGrid
                  columns={
                    activeSectionState.fields.length > 1
                      ? { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }
                      : '1fr'
                  }
                >
                  {activeSectionState.fields.map((field) => (
                    <CompactFieldRow
                      key={field.field}
                      label={field.label.replace(' *', '')}
                      value={config[field.field].value}
                      onChange={fieldHandlers[field.field]}
                      options={textParameterOptions}
                      help={field.help}
                      error={getFieldError(field.field)}
                      validationFocused={isValidationFocused(...field.focusTokens)}
                      required={field.required}
                    />
                  ))}
                </MappingFieldGrid>
              </Box>
            </EditorSurface>

            {textParameterOptions.length === 0 && (
              <WorkflowNotice severity="info">
              Load shared parameter definitions to browse available names, or type exact Revit parameter names.
              </WorkflowNotice>
            )}
          </Box>
        </WorkflowStepShell>
      </MainLayout>

      <WizardSnackbarAlert
        open={notification.open}
        onClose={handleNotificationClose}
        severity="warning"
        title="Category mappings required"
      >
        {notification.message}
      </WizardSnackbarAlert>
    </>
  );
};

export default AdditionalCategoriesPage;
